import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private resultGetAvailability: string;
  public dateCalendar: string;
  public hour: string;
  public plant: string;
  public address: string;
  public vehiclePatent: string;
  public vehicleType: string;
  public fuelType: string;
  public contactName: string;
  public phoneNumber: string;
  public email: string;
  public reference: string;

  // Variables para reserva por planta
  public plantSelected: string;
  public addressSelected: string;

  // Variables reagendar
  public changeContactName: string;
  public changeEmail: string;
  public changePhoneNumber: string;
  public changeSelectedDate: string;
  public changeSelectedHour: string;
  public changeVehiclePatent: string;
  public changeVehicleType: string;
  public codePlant: string;
  public codeType: string;
  public reservationCode: string;
  public tarifa: string;
  public latitude: string;
  public longitude: string;
  public typeReser: string;

  constructor(private http: HttpClient) { }

  addAvailability(date) {
    this.setDate(date);
    const data = { date };
    return this.http.post<any>(environment.APIEndpoint + environment.microservices.booking + 'booking-availability', data);
  }

  setDate(date) {
    this.dateCalendar = date;
  }

  /* Gets the availability for a date */
  getBookingAvailability(date): Observable<any> {
    this.dateCalendar = date;
    const data = { date };
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.booking}booking-availability`, data);
  }
  /* Gets the availability for a plant based on a date */
  getBookingAvailabilityByPlant(date, plant): Observable<any> {
    const data = { date, plant };
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.booking}booking-availability`, data);
  }
  /* Gets the availability for a plant based on a init date and end date */
  getAvailabilityByPlantAndMonth(init_date, end_date, plant = undefined): Observable<any> {
    const data = { init_date: init_date, end_date: end_date, plant: plant };
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.booking}booking-availability-plant-month`, data);
  }
   /* Gets the availability per month */
   getCalendarAvailability(month, year, plant = undefined): Observable<any> {
    const data = {month: month, year: year, plant: plant};
   return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.plant}plant-manager/get/calendar`, data);
  }
  /* Verify if a registered plate (patente) has a booking */
  verifyBooking(code): Observable<any> {
    return this.http.get<any>(`${environment.APIEndpoint}${environment.microservices.searchBooking}booking/${code}`);
  }

  /* Sets a state that indicates availability if a booking */
  setAvailability(data) {
    this.resultGetAvailability = JSON.stringify(data);
  }
  /* Gets a state that indicates availability if a booking */
  getAvailability(): Observable<string> {
    return of(this.resultGetAvailability);
  }

  /* Sets the selected plant from search-type-selection */
  setPlantSelected(plant, address, reference, codePlant, latitude, longitude) {
    this.plantSelected = plant;
    this.addressSelected = address;
    this.reference = reference;
    this.codePlant = codePlant;
    this.latitude = latitude;
    this.longitude = longitude;
  }
  /* Gets the selected plant from select-plan */
  getDataPlantAdressSelected(): Observable<any> {
    return of({
      plant: this.plantSelected,
      address: this.addressSelected,
      reference: this.reference,
      codePlant: this.codePlant,
      latitude: this.latitude,
      longitude: this.longitude
    });
  }

  /* Sets the reservation code from personal-information */
  setReservationCode(code) {
    this.reservationCode = code;
  }

  /*  */
  getDate(): Observable<any> {
    return of(this.dateCalendar);
  }

  /* Pre-saves the booking - Gets a reservation key */
  savePreBooking(date, hour, plant, address, reference, codePlant, latitude, longitude): Observable<any> {
    this.setPlantPrebookingData(date, hour, plant, address, reference, codePlant, latitude, longitude);
    const data = { date, hour, plant };
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.prebooking}booking-checkout`, data);
  }
  /* Sets the pre-booking data of a plant */
  setPlantPrebookingData(date, hour, plant, address, reference, codePlant, latitude, longitude) {
    this.dateCalendar = date;
    this.plant = plant;
    this.hour = hour;
    this.address = address;
    this.reference = reference;
    this.codePlant = codePlant;
    this.latitude = latitude;
    this.longitude = longitude;
  }
  /* Gets the prebooking data from a plant (from select-plant and select-datetime) */
  getPlantPrebookingData(): Observable<any> {
    return of({
      date: this.dateCalendar,
      plant: this.plant,
      hour: this.hour,
      address: this.address,
      reference: this.reference,
      code_plant: this.codePlant
    });
  }

  /* Saves the final booking */
  saveBooking(patent, name, phone, email, type, tarifa, typeReser, future_notification): Observable<any> {
    const dateTime = this.dateCalendar.split('T');
    const data = {
      plant: this.plant,
      vehicle_patent: patent,
      vehicle_type: type,
      selected_date: dateTime[0],
      selected_hour: this.hour,
      contact_name: name,
      phone_number: phone,
      email,
      future_notification: future_notification
    };
    this.setFinalData(patent, name, phone, email, type, tarifa, typeReser);
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.savebooking}booking`, data);
  }

  /* Sets part of the final data of a booking */
  setFinalData(patent, name, phone, email, type, tarifa, typeReser) {
    this.vehiclePatent = patent;
    this.contactName = name;
    this.phoneNumber = phone;
    this.email = email;
    this.vehicleType = type;
    this.tarifa = tarifa;
    this.typeReser = typeReser;
  }

  /* Gets the booking data from booking-confirmed and payment */
  getBookingData(): Observable<any> {
    return of({
      plant: this.plant,
      vehicle_patent: this.vehiclePatent,
      vehicle_type: this.vehicleType,
      selected_date: this.dateCalendar,
      selected_hour: this.hour,
      contact_name: this.contactName,
      phone_number: this.phoneNumber,
      email: this.email,
      address_plant: this.address,
      reference: this.reference,
      code_plant: this.codePlant,
      code_type: this.codeType,
      reservation_code: this.reservationCode,
      tarifa: this.tarifa,
      latitude: this.latitude,
      longitude: this.longitude,
      type_reser: this.typeReser
    });
  }

  /* Gets no-working-days (holydays) */
  getNotWorked(year, month): Observable<any> {
    return this.http.get(`${environment.APIEndpoint}${environment.microservices.rules}booking-rules/days-not-worked/${year}/${month}`);
  }

  /* Gets the availability of a booking through year and month */
  getDayFull(year, month): Observable<any> {
    return this.http.get(`${environment.APIEndpoint}${environment.microservices.booking}booking-availability/year-month/${year}/${month}`);
  }

  /* Gets the existent plants */
  getPlants(): Observable<any> {
    return this.http.get<any>(`${environment.APIEndpoint}${environment.microservices.plant}plant-manager`);
  }

  getByReservationCode(reservation_code: string): Observable<any> {
    var url  =`${environment.APIEndpoint}${environment.microservices.searchBooking}booking/reservation/${reservation_code}`;
    return this.http.get<any>(url);
  }

  /* Sets the booking data that has changed from change-booking */
  setDataChangeBooking(name, email, phone, date, hour, patent, type) {
    this.changeContactName = name;
    this.changeEmail = email;
    this.changePhoneNumber = phone;
    this.changeSelectedDate = date;
    this.changeSelectedHour = hour;
    this.changeVehiclePatent = patent;
    this.changeVehicleType = type;
  }
  /* Gets the changed booking data from datetime-change-booking, personal-information and select-datetime */
  getDataChangeBooking(): Observable<any> {
    return of({
      name: this.changeContactName,
      email: this.changeEmail,
      phone: this.changePhoneNumber,
      date: this.changeSelectedDate,
      hour: this.changeSelectedHour,
      patent: this.changeVehiclePatent,
      type: this.changeVehicleType
    });
  }

  /* Patches the changed/edited booking data */
  changeBooking(patent, name, phone, email, type, codeType, typeReser): Observable<any> {
    const dateTime = this.dateCalendar.split('T');
    const data = {
      plant: this.plant,
      vehicle_type: type,
      selected_date: dateTime[0],
      selected_hour: this.hour,
      contact_name: name,
      phone_number: phone,
      email
    };
    this.setFinalData(patent, name, phone, email, type, codeType, typeReser);
    return this.http.patch<any>(`${environment.APIEndpoint}${environment.microservices.changeBooking}booking/${patent}`, data);
  }

  /* Patches something to send an email? :v */
  sendMail(): Observable<any> {
    const data = {};
    return this.http.patch<any>(`${environment.APIEndpoint}${environment.microservices.sendMail}booking/mail/${this.vehiclePatent}`, data);
  }

  /*  */
  resetVariables() {
    this.resultGetAvailability = undefined;
    this.dateCalendar = undefined;
    this.hour = undefined;
    this.plant = undefined;
    this.address = undefined;
    this.vehiclePatent = undefined;
    this.vehicleType = undefined;
    this.fuelType = undefined;
    this.contactName = undefined;
    this.phoneNumber = undefined;
    this.email = undefined;

    // Variables para reserva por planta
    this.plantSelected = undefined;
    this.addressSelected = undefined;

    // Variables reagendar
    this.changeContactName = undefined;
    this.changeEmail = undefined;
    this.changePhoneNumber = undefined;
    this.changeSelectedDate = undefined;
    this.changeSelectedHour = undefined;
    this.changeVehiclePatent = undefined;
    this.changeVehicleType = undefined;
    this.reference = undefined;
    this.codePlant = undefined;
    this.codeType = undefined;
    this.tarifa = undefined;
    this.latitude = undefined;
    this.longitude = undefined;
    this.typeReser = undefined;
    // this.channelType = undefined;
  }

  /* Saves the payment information */
  paymentFinaly(code, amount, patent, phone, email, run, name, address, region, giro, commune): Observable<any> {
    const data: any = {
      document_code: code,
      amount,
      patent,
      phone,
      reservation_code: this.reservationCode,
      email,
      run,
      name
    };
    sessionStorage.setItem("dataPago",JSON.stringify( data));
    if (code === '33') {
      data.business_name = name;
      data.address = address;
      data.region = region;
      data.business_activity = giro;
      data.commune = commune;
    }
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.payments}booking-tax-document`, data);
  }

  updateReservation () {
    var infoPago = JSON.parse(sessionStorage.getItem("dataPago"))

    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.payments}booking-tax-document/reservation`, {
      data : infoPago, 
      reservation_code:  infoPago.reservation_code, 
      document_code: infoPago.document_code
    });
  }

 
  /* Gets the webpay url to redirect the user */
  paymentWebPay() {
    const data = {
      amount: this.tarifa.replace(/\./g, ''),
      reservation_code: this.reservationCode
    };
    // return this.http.post<any>(`https://agendamiento.revisionestecnicas.cl/api/ms-sgs-payment/pay`, data);
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.webpay}pay`, data);
  }
  verifyWebPay(token_ws: string, reservationCode: string) {
    const data = {
      token: token_ws,
      reservation_code: reservationCode
    };
    // return this.http.post<any>(`https://agendamiento.revisionestecnicas.cl/api/ms-sgs-payment/pay`, data);
    return this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.webpay}verify`, data);
  }
  /* Gets the available vehicle types */
  getVehicleTypes() {
    return this.http.get<any>(`${environment.APIEndpoint}${environment.microservices.typesInspection}technical-inspection/types/get`);
  }

  /* Gets prices types */
  getPrice(plantCode, technicalCode) {
    const data = {
      plant_code: plantCode,
      prt_technical_code: technicalCode
    };
    return (
      this.http.post<any>(`${environment.APIEndpoint}${environment.microservices.typesInspection}technical-inspection/price/get`, data)
    );
  }

  /* Sets the tax gotten from user's interaction */
  setTarifa(tarifa) {
    this.tarifa = tarifa;
  }

  /* Gets the available regions and communes */
  getRegionAndCommune(): Observable<any> {
    return this.http.get(`${environment.APIEndpoint}${environment.microservices.rules}booking-rules/region-and-commune`);
  }
}
// feliperoblez@gmail.com
